import React from "react";

import DynamicAdminTable, { Column } from "../../components/admin-table";
import Topbar from "../../components/topbar";
import { useListSearch, ListTitle } from "../../components/ui/list-search";
import AddCompanyPopup from "../../components/popup/add-company";
import DeletePopup from "../../components/popup/delete";
import AddUserPopup from "../../components/popup/add-user";
import CompanyDetailsModal from "../../components/popup/company-details";

import Pagination from "../../components/pagination";
import AddBtn from "../../components/ui/add-btn";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Radio, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import Cookies from 'js-cookie';
import LoadingOverlay from "../../components/loading-overlay";
import Toggle from "../../components/ui/toggle";
import impersonateImage from '../../files/images/impersonate.png';
import companyImage from '../../files/images/company.png';
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const PAGE_SIZE = 10;

// What the search matches on - the visible company details.
const companySearchText = (row: Record<string, any>) => [row.company, row.country, row.phone, row.loginNotifyEmail];

// Filter presets above the table - same pattern as Subscription Transactions.
const PRESETS: { key: string; label: string; match: (row: Record<string, any>) => boolean }[] = [
    { key: 'all', label: 'All', match: () => true },
    { key: 'permitted', label: 'Login Permitted', match: row => !!row.access },
    { key: 'restricted', label: 'Login Restricted', match: row => !row.access },
    { key: 'closed', label: 'Closed', match: row => !row.account },
    { key: 'alerts', label: 'Login Alerts On', match: row => !!row.loginNotifyEnabled },
];

type Company = {
    company: string;
    country: string;
    phone: string;
    banks: number;
    trx: number;
    payment: string;
    created: string;
    lastLogin: string;
    access: boolean;
    account: boolean;
    loginNotifyEnabled: boolean;
    loginNotifyEmail: string;
};

const Company: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [page, setPage] = React.useState(1);
    const [preset, setPreset] = React.useState('all');
    const [data, setData] = React.useState<Company[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [detailsCompany, setDetailsCompany] = React.useState<Record<string, any> | null>(null);

    const [fetch, setFetch] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [toggleAccess, setToggleAccess] = useApi();
    const [toggleEnabled, setToggleEnabled] = useApi();
    const [saveLoginAlert, setSaveLoginAlert] = useApi();
    // Impersonate from a company's row: pick one of its users, and the session
    // opens in this company (same flow as the Impersonate page).
    const [impersonateCompany, setImpersonateCompany] = React.useState<Record<string, any> | null>(null);
    const [impersonateUserGuid, setImpersonateUserGuid] = React.useState('');
    const [fetchCompanyUsers, setFetchCompanyUsers] = useApi();
    const [startImpersonation, setStartImpersonation] = useApi();
    // Login alerts: the company whose alert address is being changed, and the address typed.
    const [alertDialog, setAlertDialog] = React.useState<{ row: Record<string, any>; email: string } | null>(null);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.company.fetch);
        setFetch({ url, method });
    }, [page, update, remove, toggleAccess, toggleEnabled, saveLoginAlert]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data as Company[]);
        }
        if (error) {
            showError('Error fetching company data');
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggleAccess;
        if (data && !isLoading) {
            const { url, method } = buildApiRequestConfig(routes.company.fetch);
            setFetch({ url, method });
        }
        if (error) {
            showError('Error updating access');
        }
    }, [toggleAccess, showError, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggleEnabled;
        if (data && !isLoading) {
            const { url, method } = buildApiRequestConfig(routes.company.fetch);
            setFetch({ url, method });
        }
        if (error) {
            showError('Error updating account status');
        }
    }, [toggleEnabled, showError, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = saveLoginAlert;
        if (data && !isLoading) {
            showSuccess(data.loginNotifyEnabled ? `Login alerts on - sending to ${data.loginNotifyEmail}` : 'Login alerts off');
            setAlertDialog(null);
        }
        if (error) showError(typeof error === 'string' ? error : 'Error updating login alerts');
    }, [saveLoginAlert]); // eslint-disable-line react-hooks/exhaustive-deps

    const openImpersonate = (row: Record<string, any>) => {
        setImpersonateCompany(row);
        setImpersonateUserGuid('');
        const { url, method } = buildApiRequestConfig(routes.impersonate.companyUsers, [{ param: 'entityId', value: row.id }]);
        setFetchCompanyUsers({ url, method });
    };
    const companyUsers: { guid: string; name: string | null; email: string; role: string }[] = impersonateCompany && fetchCompanyUsers.data?.users ? fetchCompanyUsers.data.users : [];

    // Admins come first - start with the first one picked.
    React.useEffect(() => {
        if (companyUsers.length && !impersonateUserGuid) setImpersonateUserGuid(companyUsers[0].guid);
    }, [fetchCompanyUsers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const { data, isLoading, error } = startImpersonation;
        if (data && !isLoading) {
            // useApi re-sends its last request when the token changes - clear
            // these first, or they go out again as the impersonated user
            // (not an admin) and fail with "Only admins can impersonate users".
            setStartImpersonation(null);
            setFetchCompanyUsers(null);
            Cookies.set('token', data.token);
            Cookies.set('user', JSON.stringify(data.user));
            Cookies.set('entities', JSON.stringify(data.entities || []));
            // A full load into the user portal, not an in-app navigate - the
            // identity just changed, and anything still holding the admin's
            // state (open requests, AuthGuard's timer on this /admin page)
            // could otherwise act on the new cookies and bounce to a login page.
            window.location.assign('/transaction');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to start impersonation');
    }, [startImpersonation]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStartImpersonation = () => {
        if (!impersonateCompany || !impersonateUserGuid) return;
        const { url, method } = buildApiRequestConfig(routes.impersonate.start);
        setStartImpersonation({ url, method, body: { targetUserGuid: impersonateUserGuid, entityId: impersonateCompany.id } });
    };

    const presetCounts = React.useMemo(() => Object.fromEntries(PRESETS.map(p => [p.key, data.filter(p.match).length])), [data]);
    const presetData = React.useMemo(() => data.filter((PRESETS.find(p => p.key === preset) || PRESETS[0]).match), [data, preset]);
    // Search within the chosen preset - by what's shown in the table.
    const list = useListSearch(presetData, companySearchText);
    const filteredData = list.filtered;
    React.useEffect(() => { setPage(1); }, [list.term]);

    const sendLoginAlert = (row: Record<string, any>, body: { enabled?: boolean; email?: string }) => {
        const { url, method } = buildApiRequestConfig(routes.company.loginNotification, [{ param: 'id', value: row.id }]);
        setSaveLoginAlert({ url, method, body });
    };

    // On sends to the company's address, or the admin inbox (the one new
    // signups go to) when it has none; off keeps the address for next time.
    const handleLoginAlertToggle = (row: Record<string, any>, value: boolean) => {
        sendLoginAlert(row, { enabled: value });
    };

    const alertEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(alertDialog?.email.trim() || '');

    const handleAccessToggle = (row: Record<string, any>, value: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.company.toggleAccess, [{ param: 'id', value: row.id }]);
        setToggleAccess({ url, method });
        if (value) {
            showSuccess('Access enabled');
        } else {
            showSuccess('Access disabled');
        }
    };

    const handleAccountToggle = (row: Record<string, any>, value: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.company.toggleEnabled, [{ param: 'id', value: row.id }]);
        setToggleEnabled({ url, method });
        if (value) {
            showSuccess('Account enabled');
        } else {
            showSuccess('Account disabled');
        }
    };

    const columns: Column[] = [
        { id: 'company', label: 'Company', size: 100, onSort: (direction) => console.log(`Sort by createdAt ${direction}`) },
        { id: 'country', label: 'Country', size: 100, onSort: (direction) => console.log(`Sort by createdAt ${direction}`) },
        { id: 'phone', label: 'Contact Number', size: 100, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'banks', label: 'Bank Accounts', size: 50, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'trx', label: "TX's", size: 50, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'payment', label: 'Payment Status', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'created', label: 'Created', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'lastLogin', label: 'Last Login', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'access', label: 'Access', size: 150, type: 'multi-toggle', sibling: 'account', toggleLabels: { on: 'Login Permitted', off: 'Login Restricted' }, onToggle: handleAccessToggle },
        { id: 'account', label: 'Account', size: 150, type: 'multi-toggle', sibling: 'access', toggleLabels: { on: 'Open', off: 'Closed' }, onToggle: handleAccountToggle },
        // Rendered below (loginAlertCell) - the toggle plus the address it sends to.
        { id: 'loginAlert', label: 'Login Alerts', size: 180 },
        {
            id: 'actions',
            label: 'Actions',
            size: 50,
            type: 'actions',
            onEdit: (row) => setDetailsCompany(row),
            editLabel: 'View',
            onDelete: (row) => { setOpenModal('delete'); console.log('Delete', row);  },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddCompanyPopup
                open={openModal === 'add'}
                onClose={() => setOpenModal('')}
                onConfirm={(name, amount, enabled) => {
                    console.log('New company added:', { name, amount, enabled });
                    setOpenModal('');
                }}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Company?"
                description="This action will permanently remove the selected company."
                image={{ src: companyImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    setOpenModal('');
                }}
            />
            <LoadingOverlay
                isLoading={startImpersonation.isLoading}
                title="Starting Impersonation..."
                message="Please wait while we switch to the user account."
            />
            <Dialog open={!!impersonateCompany} onClose={() => setImpersonateCompany(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Impersonate - {impersonateCompany?.company}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                        <img src={impersonateImage} alt="" width={64} height={64} />
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                            You'll be signed in as the selected user, in this company. Your admin session ends - sign in again here when you're done.
                        </Typography>
                    </Box>
                    {fetchCompanyUsers.isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                    ) : companyUsers.length === 0 ? (
                        <Typography sx={{ fontSize: 13, py: 2, textAlign: 'center' }}>This company has no users to impersonate.</Typography>
                    ) : (
                        <Box sx={{ border: '1px solid #e9e9e9', borderRadius: 2, maxHeight: 320, overflowY: 'auto' }}>
                            {companyUsers.map(u => (
                                <Box
                                    key={u.guid}
                                    onClick={() => setImpersonateUserGuid(u.guid)}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: 'pointer', borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' }, background: impersonateUserGuid === u.guid ? 'rgba(var(--color-primary-rgb), 0.08)' : 'transparent' }}
                                >
                                    <Radio size="small" checked={impersonateUserGuid === u.guid} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{u.name || u.email}</Typography>
                                        {u.name && <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{u.email}</Typography>}
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{u.role}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setImpersonateCompany(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!impersonateUserGuid || startImpersonation.isLoading}
                        onClick={handleStartImpersonation}
                        sx={{ textTransform: 'none', boxShadow: 'none' }}
                    >
                        Start Impersonation
                    </Button>
                </DialogActions>
            </Dialog>
            <AddUserPopup
                open={openModal === 'invite'}
                onClose={() => setOpenModal('')}
                onCreate={(newUser) => {
                    setOpenModal('');
                }}
            />
            <CompanyDetailsModal
                open={!!detailsCompany}
                company={detailsCompany}
                onClose={() => setDetailsCompany(null)}
            />
            <Dialog open={!!alertDialog} onClose={() => setAlertDialog(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Login alerts - {alertDialog?.row.company}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mb: 2 }}>
                        An email goes to this address whenever someone in this company signs in.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Send alerts to"
                        placeholder="name@example.com"
                        value={alertDialog?.email ?? ''}
                        onChange={e => setAlertDialog(prev => prev ? { ...prev, email: e.target.value } : prev)}
                        error={!!alertDialog?.email.trim() && !alertEmailValid}
                        helperText={alertDialog?.email.trim() && !alertEmailValid ? 'Enter a valid email address' : ' '}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAlertDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!alertEmailValid || saveLoginAlert.isLoading}
                        onClick={() => alertDialog && sendLoginAlert(alertDialog.row, { email: alertDialog.email.trim() })}
                        sx={{ textTransform: 'none', boxShadow: 'none' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Topbar title={<ListTitle title="Company" list={list} placeholder="Search companies..." />} />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={preset}
                        onChange={(_, v) => { if (v) { setPreset(v); setPage(1); } }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75 } }}
                    >
                        {PRESETS.map(p => (
                            <ToggleButton key={p.key} value={p.key}>{p.label} ({presetCounts[p.key] ?? 0})</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <AddBtn onClick={() => setOpenModal('add')} label="Add Company" />
                </Box>
                <DynamicAdminTable 
                    columns={columns} 
                    data={filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(row => ({
                        ...row,
                        loginAlert: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Toggle checked={!!row.loginNotifyEnabled} onChange={(val: boolean) => handleLoginAlertToggle(row, val)} />
                                {row.loginNotifyEmail ? (
                                    <Typography
                                        title="Change the address"
                                        onClick={() => setAlertDialog({ row, email: row.loginNotifyEmail })}
                                        sx={{ fontSize: 12, color: row.loginNotifyEnabled ? 'var(--color-primary)' : 'var(--color-text-muted)', cursor: 'pointer', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {row.loginNotifyEmail}
                                    </Typography>
                                ) : (
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Off</Typography>
                                )}
                            </Box>
                        ),
                    }))}  
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={Math.ceil(filteredData.length / PAGE_SIZE)}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={filteredData.length}
                        />
                    }
                    additionalActions={[ 
                        { label: 'Impersonate', onClick: openImpersonate, icon: 'eye' },
                        { label: 'Invite a User', onClick: () => setOpenModal('invite'), icon: 'invite' },
                    ]}
                />
            </div>
        </div>
    );
};

export default Company;